import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CountUp from 'react-countup';
import isEqual from 'lodash/isEqual';
import Step from '../../common/step';
import RoleCard from './RoleCard';
import InfoCard from './InfoCard';
import { fetchPaygradeAsync, updateField } from '../../../actions/BookingRequest';
import { toggleRolePopout } from '../../../actions/ViewController';
import { durationBetweenDates, getTime } from '../../../api/DataHandlers';
import { validateValues } from '../../common/Utils';
import history from '../../../history';
import { TRootState } from '../../../reducers';
import { usePrevious } from '../../../hooks';


export default function RequestModules() {
    const dispatch = useDispatch();

    const {
        bookingRequest,
        minStart,
        paygrade,
        roleData,
        rolePopout,
    } = useSelector((state: TRootState) => ({
        bookingRequest: state.requestData.bookingRequest,
        minStart: state.requestData.minStart,
        paygrade: state.requestData.paygrade,
        roleData: state.requestData.roleData,
        rolePopout: state.view.rolePopout,
    }));

    const prevMinStart = usePrevious(minStart);
    const prevBookingRequest = usePrevious(bookingRequest);
    const prevPaygrade = usePrevious(paygrade);
    const prevRoleData = usePrevious(roleData);

    useEffect(() => {
        dispatch(updateField('start_time', '17:00'));
        dispatch(updateField('duration', 4));
    }, []);

    useEffect(() => {
        if (prevMinStart !== minStart) {
            const newMinStart = getTime(minStart);
            const startTime = parseInt(newMinStart, 10) >= 17
                ? newMinStart
                : '17:00';
            dispatch(updateField('start_time', startTime));
        }
    }, [minStart]);

    useEffect(() => {
        if (
            prevBookingRequest?.date !== bookingRequest.date
            || prevBookingRequest?.service !== bookingRequest.service
            || !isEqual(prevRoleData, roleData)
        ) {
            const valid = validateValues([bookingRequest.date, bookingRequest.service]);
            if (valid) {
                dispatch(fetchPaygradeAsync(
                    bookingRequest.service,
                    bookingRequest.start_time,
                    bookingRequest.date,
                    roleData[bookingRequest.service],
                ));
            }
        }
    }, [bookingRequest.date, bookingRequest.service, roleData.construction, roleData.driver, roleData.hospitality,
        roleData.industry, roleData.office]);

    const animationDuration = 1;
    const oldFreelancersAvailable = prevPaygrade?.freelancers_available || 0;
    const oldAverageRating = prevPaygrade?.average_rating || 0;
    const oldEstimatedMatching = prevPaygrade?.estimated_matching
        ? Math.round(durationBetweenDates(new Date(), prevPaygrade.estimated_matching, 1))
        : 0;
    const oldClientPayPerHour = prevPaygrade?.client_pay_per_hour || 0;

    const freelancersAvailable = paygrade?.freelancers_available
        ? (
            <CountUp
                start={oldFreelancersAvailable}
                end={paygrade.freelancers_available}
                duration={animationDuration}
            />
        ) : '-';

    const averageRating = paygrade?.average_rating
        ? (
             <span>
                <CountUp
                    start={oldAverageRating}
                    end={paygrade.average_rating}
                    decimals={1}
                    duration={animationDuration}
                />
                &nbsp;<span className="small">out of 5</span>
            </span>
        ) : '-';

    const estimatedMatching = paygrade?.estimated_matching
        ? (
            <CountUp
                start={oldEstimatedMatching}
                end={Math.round(durationBetweenDates(new Date(), paygrade.estimated_matching, 1))}
                suffix=" mins"
                duration={animationDuration}
            />
        ) : '-';

    const clientPayPerHour = paygrade?.client_pay_per_hour
        ? (
            <CountUp
                start={oldClientPayPerHour}
                end={paygrade.client_pay_per_hour}
                decimals={2}
                prefix="£"
                suffix=" /h"
                duration={animationDuration}
            />
        ) : '-';

    const paygradeValid = paygrade
        && paygrade.freelancers_available
        && paygrade.average_rating
        && paygrade.estimated_matching
        && paygrade.client_pay_per_hour;

    const cardsContainerClassName =
        `cards-container hidden-xs${rolePopout.shiftTimes || rolePopout.roleExtras ? ' cards-container-expanded' : ''}`;

    const handleCreateRequest = () => {
        dispatch(toggleRolePopout('roleExtras', true));
        history.push({
            pathname: '/job/create',
            state: { mode: 'signup' },
        });
    };

    return (
        <div className="section section--request-modules">
            <div className="section-bg" />
            <h1>Great staff, at your fingertips.</h1>
            <Step />
            <div className="cards-container cards-container-xs visible-xs">
                <RoleCard />
                <div className="card card-info-xs">
                    <div className="info-item">
                        <i className="icon icon-phone-book-1" />
                        <p className="info-detail">
                            Workers
                            <br />
                            {freelancersAvailable}
                        </p>
                    </div>
                    <div className="info-item">
                        <i className="icon icon-star-empty" />
                        <p className="info-detail">
                            Average rating
                            <br />
                            {averageRating}
                        </p>
                    </div>
                    <div className="info-item">
                        <i className="icon icon-stopwatch" />
                        <p className="info-detail">
                            Matched in
                            <br />
                            {estimatedMatching}
                        </p>
                    </div>
                    <div className="info-item">
                        <i className="icon icon-credit-card" />
                        <p className="info-detail">
                            Est. cost
                            <br />
                            {clientPayPerHour}
                        </p>
                    </div>
                </div>
            </div>
            <div className={cardsContainerClassName}>
                <RoleCard />
                <div className="card-info-wrapper">
                    <InfoCard
                        className="card-info-available"
                        title="Workers available">
                        {freelancersAvailable}
                    </InfoCard>
                    <InfoCard
                        className="card-info-match"
                        title="Matched within"
                    >
                        {estimatedMatching}
                    </InfoCard>
                </div>
                <div className="card-info-wrapper">
                    <InfoCard
                        className="card-info-rating"
                        title="Average workers rating"
                    >
                        {averageRating}
                    </InfoCard>
                    <InfoCard
                        className="card-info-cost"
                        title="Estimated cost"
                    >
                        {clientPayPerHour}
                    </InfoCard>
                </div>
            </div>
            <button
                className={`btn-create-request${!paygradeValid ? ' disabled' : ''}`}
                type="button"
                onClick={handleCreateRequest}
            >
                CREATE REQUEST
            </button>
        </div>
    );
}
